import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Lightweight hook that silently ensures the recommendation cache is populated.
 * Runs once on mount - "fire and forget" with no UI state returned.
 * 
 * Triggers background generation if:
 * - User has at least 3 rated books
 * - Cache has fewer than 15 pending recommendations
 */
export function useRecommendationPrefetch() {
  const hasPrefetched = useRef(false);

  useEffect(() => {
    // Only run once per component lifecycle
    if (hasPrefetched.current) return;
    hasPrefetched.current = true;

    const prefetch = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('[Prefetch] No session, skipping');
        return;
      }

      // Check if user has minimum reviews (3+)
      const { count: reviewCount } = await supabase
        .from('user_books')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .not('rating_star', 'is', null)
        .gt('rating_star', 0);

      if ((reviewCount || 0) < 3) {
        console.log('[Prefetch] User has fewer than 3 reviews, skipping');
        return;
      }

      // Prefetch award recommendations into React Query cache
      const { count: awardCount } = await supabase
        .from('user_cached_recommendations')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .eq('recommendation_type', 'award')
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString());

      if ((awardCount || 0) < 4) {
        console.log('[Prefetch] Low award cache, triggering award recs fetch');
        supabase.functions.invoke('recommend-award-books', {
          body: {},
          headers: { Authorization: `Bearer ${session.access_token}` }
        }).catch(err => console.error('[Prefetch] Award fetch error:', err));
      }

      // Check current cache count of pending recommendations
      const { count: cacheCount } = await supabase
        .from('user_cached_recommendations')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString());

      // Only trigger generation if cache is low
      if ((cacheCount || 0) < 15) {
        console.log('[Prefetch] Low cache count:', cacheCount, '- triggering background generation');
        
        // Fire and forget - don't await or handle response
        supabase.functions.invoke('generate-cached-recommendations', {
          body: { triggerType: 'prefetch', targetCount: 25 }
        }).then(({ error }) => {
          if (error) {
            console.error('[Prefetch] Background generation error:', error);
          } else {
            console.log('[Prefetch] Background generation triggered successfully');
          }
        }).catch(err => {
          console.error('[Prefetch] Unexpected error:', err);
        });
      } else {
        console.log('[Prefetch] Cache is healthy:', cacheCount, 'pending recommendations');
      }
    };

    prefetch().catch(console.error);
  }, []);
}

/**
 * Standalone prefetch function for use outside of React components.
 * Called from AuthProvider after sign-in.
 */
export async function prefetchRecommendations(): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Check if user has minimum reviews
    const { count: reviewCount } = await supabase
      .from('user_books')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', session.user.id)
      .not('rating_star', 'is', null)
      .gt('rating_star', 0);

    if ((reviewCount || 0) < 3) return;

    // Check current cache count
    const { count: cacheCount } = await supabase
      .from('user_cached_recommendations')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', session.user.id)
      .eq('status', 'pending')
      .gt('expires_at', new Date().toISOString());

    if ((cacheCount || 0) < 15) {
      console.log('[Auth Prefetch] Triggering background recommendation generation');
      
      await supabase.functions.invoke('generate-cached-recommendations', {
        body: { triggerType: 'auth_prefetch', targetCount: 25 }
      });
    }
  } catch (error) {
    console.error('[Auth Prefetch] Error:', error);
  }
}
